import { useEffect, useState } from 'react';
import { getDailyTestGate } from '../api/dailyTest';

// Remembers that this student was walled last time, so the wall can be shown
// immediately on the next open instead of flashing homework first. Same trick,
// and same rationale, as DailyTestCard's `dailyTest:seen:` key.
const BLOCKED_KEY = 'dailyTestGate:blocked:';

function readWasBlocked(studentId) {
  try {
    return localStorage.getItem(BLOCKED_KEY + studentId) === '1';
  } catch {
    return false;
  }
}

function writeWasBlocked(studentId, blocked) {
  try {
    if (blocked) localStorage.setItem(BLOCKED_KEY + studentId, '1');
    else localStorage.removeItem(BLOCKED_KEY + studentId);
  } catch {
    // Private mode / quota — this is a rendering optimisation, never the gate itself.
  }
}

/**
 * Is this student currently walled out of their homework by unpaid daily tests?
 *
 * Two states matter to a caller, and they are deliberately separate:
 *
 *  - `gated`  — show the wall.
 *  - `holdRender` — do not paint homework yet, we are still asking.
 *
 * `holdRender` is true ONLY for a student we already saw walled (remembered in
 * localStorage). That asymmetry is the whole design: a child who is being
 * blocked should never watch their homework appear and then get snatched away,
 * while the overwhelming majority who are not gated must not pay a network
 * round trip before their homework paints. The app's existing paywall accepts
 * the flash for everyone; a gate aimed at one child does not have to.
 *
 * Fails OPEN. A gate is a scheduling nudge, not a security boundary — a network
 * blip must never be the reason a child cannot do their homework.
 *
 * @param {string|null|undefined} studentId
 * @param {{ skip?: boolean }} [options] - skip for teacher preview / teacher / admin
 * @returns {{ gated: boolean, owed: number, holdRender: boolean }}
 */
export function useHomeworkGate(studentId, { skip = false } = {}) {
  const [status, setStatus] = useState(null);
  // Read once per student, before any fetch, so the very first paint can already
  // hold for a student we know was blocked.
  const [wasBlocked, setWasBlocked] = useState(() => (skip || !studentId ? false : readWasBlocked(studentId)));

  useEffect(() => {
    setWasBlocked(skip || !studentId ? false : readWasBlocked(studentId));
  }, [studentId, skip]);

  useEffect(() => {
    if (skip || !studentId) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    // Drop the previous student's verdict BEFORE asking about this one. Without
    // this, switching between siblings shows child B the wall that belongs to
    // child A — the one direction this hook must never fail in.
    setStatus(null);
    getDailyTestGate(studentId)
      .then((res) => {
        if (cancelled) return;
        setStatus(res);
        writeWasBlocked(studentId, !!res?.gated);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open, and stop holding render — never strand a child on a loader
        // because the gate endpoint is unreachable.
        //
        // The remembered-blocked hint is left ALONE. Clearing it would mean one
        // flaky request costs the next session its hold, and a genuinely gated
        // child would get the flash of homework this hook exists to prevent.
        setStatus({ gated: false, owed: 0 });
      });
    return () => { cancelled = true; };
  }, [studentId, skip]);

  if (skip || !studentId) return { gated: false, owed: 0, holdRender: false };

  return {
    gated: !!status?.gated,
    owed: status?.owed || 0,
    holdRender: status === null && wasBlocked,
  };
}

export default useHomeworkGate;
